import { useEffect, useRef } from "react";
import { Image, StyleSheet, View } from "react-native";
import { Asset } from "expo-asset";

const CELLULAR_BACKGROUND = require("../../assets/images/onboarding-cellular-bg.jpg");

const VERTEX_SHADER = `
  attribute vec2 aPosition;
  varying vec2 vUv;

  void main() {
    vUv = aPosition * 0.5 + 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER = `
  precision highp float;

  uniform sampler2D uTexture;
  uniform vec2 uResolution;
  uniform vec2 uImageSize;
  uniform vec2 uCenter;
  uniform float uTime;
  uniform float uMotion;
  varying vec2 vUv;

  void main() {
    float canvasAspect = uResolution.x / uResolution.y;
    float imageAspect = uImageSize.x / uImageSize.y;
    vec2 coverScale = canvasAspect > imageAspect
      ? vec2(1.0, imageAspect / canvasAspect)
      : vec2(canvasAspect / imageAspect, 1.0);

    vec2 center = uCenter;
    vec2 radial = vUv - center;
    radial.x *= canvasAspect;
    float radius = length(radial);

    vec2 direction = vec2(0.0);
    if (radius > 0.0001) {
      direction = radial / radius;
      direction.x /= canvasAspect;
    }

    // A continuous radial wave keeps the detailed texture intact as each
    // crest passes. The train travels continuously, so no pulse is restarted.
    float ringPosition = radius * 3.65 - uTime * 0.17;
    float ringCycle = fract(ringPosition);
    float lensProfile = sin(ringCycle * 6.2831853);
    float radialFade = 1.0 - smoothstep(0.90, 1.10, radius);
    float centerFade = smoothstep(0.045, 0.15, radius);
    // Keep the textured skin recognizable: the light/dark crown carries the
    // ripple while the lens only gives the source a gentle radial bend.
    float displacement =
      lensProfile * 0.006 * radialFade * centerFade * uMotion;

    float zoom = 1.075;
    vec2 sourceUv = (vUv - 0.5) * coverScale / zoom + 0.5;
    sourceUv += direction * displacement;

    vec4 color = texture2D(uTexture, clamp(sourceUv, 0.001, 0.999));
    // The rollover gets a wide highlight and a darker trailing shoulder so
    // the ring has a visible crown rather than a thin bright outline.
    float seamDistance = min(ringCycle, 1.0 - ringCycle);
    float crestHalo = 1.0 - smoothstep(0.0, 0.14, seamDistance);
    float crestTop = 1.0 - smoothstep(0.0, 0.075, seamDistance);
    float trailingShadow =
      smoothstep(0.075, 0.14, seamDistance) *
      (1.0 - smoothstep(0.14, 0.225, seamDistance));
    float bandShade = cos(ringCycle * 6.2831853) * 0.06;
    float ringLight =
      (bandShade +
        crestHalo * 0.12 +
        crestTop * 0.24 -
        trailingShadow * 0.14) *
      radialFade *
      centerFade;
    color.rgb *= 1.0 + ringLight * uMotion;
    color.rgb += crestTop * 0.04 * radialFade * centerFade * uMotion;
    gl_FragColor = color;
  }
`;

type Props = {
  motionEnabled: boolean;
  onFirstFrame?: () => void;
  origin: { x: number; y: number } | null;
};

function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string,
) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

export default function LivingCellularBackground({
  motionEnabled,
  onFirstFrame,
  origin,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !origin) return;

    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      powerPreference: "high-performance",
    });
    if (!gl) return;

    const vertex = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertex || !fragment) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );

    const position = gl.getAttribLocation(program, "aPosition");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const resolution = gl.getUniformLocation(program, "uResolution");
    const imageSize = gl.getUniformLocation(program, "uImageSize");
    const center = gl.getUniformLocation(program, "uCenter");
    const time = gl.getUniformLocation(program, "uTime");
    const motion = gl.getUniformLocation(program, "uMotion");
    const sampler = gl.getUniformLocation(program, "uTexture");

    const texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.uniform1i(sampler, 0);

    let frame = 0;
    let disposed = false;
    let sourceWidth = 1;
    let sourceHeight = 1;
    let imageReady = false;
    let firstFrameReported = false;
    const startedAt = performance.now();

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const density = Math.min(window.devicePixelRatio || 1, 2);
      const nextWidth = Math.max(1, Math.round(rect.width * density));
      const nextHeight = Math.max(1, Math.round(rect.height * density));
      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const render = (now: number) => {
      if (disposed || !imageReady) return;
      resize();
      gl.uniform2f(resolution, canvas.width, canvas.height);
      gl.uniform2f(imageSize, sourceWidth, sourceHeight);
      // WebGL's vertical UV axis is inverted relative to screen layout.
      gl.uniform2f(center, origin.x, 1 - origin.y);
      gl.uniform1f(time, motionEnabled ? (now - startedAt) / 1000 : 2.3);
      gl.uniform1f(motion, 1);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      if (!firstFrameReported) {
        firstFrameReported = true;
        onFirstFrame?.();
      }
      if (motionEnabled) frame = requestAnimationFrame(render);
    };

    const source = Asset.fromModule(CELLULAR_BACKGROUND);
    const background = new window.Image();
    background.onload = () => {
      if (disposed) return;
      sourceWidth = background.naturalWidth;
      sourceHeight = background.naturalHeight;
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        background,
      );
      imageReady = true;
      frame = requestAnimationFrame(render);
    };
    background.src = source.uri;

    const observer = new ResizeObserver(() => {
      if (!motionEnabled && imageReady) render(performance.now());
    });
    observer.observe(canvas);

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
    };
  }, [motionEnabled, onFirstFrame, origin]);

  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      <Image
        source={CELLULAR_BACKGROUND}
        resizeMode="cover"
        style={StyleSheet.absoluteFill}
      />
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    </View>
  );
}
